import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore'; 
import { useNavigate } from 'react-router-dom';
import { statesAndDistricts } from '../data/indianStates';
import { motion } from 'framer-motion';
import YouTubeHelpButton from '../components/YouTubeHelpButton';

const Signup = () => {
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // ✅ Error Popup State
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  // ✅ FORGOT PASSWORD STATES
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  // ✅ Form Data (Login Identifier = Email or Mobile)
  const [formData, setFormData] = useState({
    name: '', mobile: '', email: '', password: '', loginIdentifier: '',
    role: 'customer', 
    state: '', district: '', village: ''
  });

  const [availableDistricts, setAvailableDistricts] = useState([]);

  // --- HANDLERS ---
  const handleChange = (e) => {
    let { name, value } = e.target;
    
    // Mobile number validation logic
    if (name === 'mobile') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length > 10) return;
      value = numericValue;
    }

    setFormData({ ...formData, [name]: value });
    if (name === 'state') setAvailableDistricts(statesAndDistricts[value] || []);
  };

  const selectRole = (role) => {
    setFormData({ ...formData, role: role });
  };

  // ✅ HANDLE PASSWORD RESET
  const handleForgotPassword = async () => {
    if (!resetEmail) {
        alert("Kripya apna registered Email ID dalein.");
        return;
    }
    
    try {
        await sendPasswordResetEmail(auth, resetEmail);
        alert(`✅ Password Reset Link sent to ${resetEmail}. Check your Inbox/Spam folder.`);
        setShowForgotModal(false); 
        setResetEmail(''); 
    } catch (error) {
        console.error("Reset Error:", error);
        if (error.code === 'auth/user-not-found') {
            alert("❌ Yeh Email registered nahi hai.");
        } else if (error.code === 'auth/invalid-email') {
            alert("❌ Galat Email format hai.");
        } else {
            alert("❌ Error: " + error.message);
        }
    }
  };

  // ✅✅ MAIN LOGIN/SIGNUP LOGIC ✅✅
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignup) {
        // -------------------------
        // 📝 REGISTER LOGIC
        // -------------------------
        if (formData.mobile.length !== 10) {
            setLoading(false);
            return alert("⚠️ Mobile Number must be 10 digits.");
        }
        if (!formData.email.includes('@')) {
            setLoading(false);
            return alert("⚠️ Valid Email ID required.");
        }

        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        
        await setDoc(doc(db, "users", userCredential.user.uid), { 
            ...formData, 
            createdAt: new Date() 
        });
        
        if(formData.role === 'worker') {
            navigate('/add-worker');
        } else {
            navigate('/');
        }

      } else {
        // -------------------------
        // 🔑 LOGIN LOGIC (Fixed for Old Users)
        // -------------------------
        let loginValue = formData.loginIdentifier.trim();
        
        // Check if input is a Mobile Number
        const isMobileNumber = /^[0-9]{10}$/.test(loginValue);
        let finalLoginEmail = loginValue;

        if (isMobileNumber) {
            // 🕵️‍♂️ Step 1: Database se user dhoondo using Mobile Number
            const q = query(collection(db, "users"), where("mobile", "==", loginValue));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                setLoading(false);
                return alert("❌ Yeh Mobile Number registered nahi hai. Naya account banayein.");
            }

            const userData = querySnapshot.docs[0].data();

            // ✅✅✅ MAGIC FIX FOR OLD USERS ✅✅✅
            if (userData.email && userData.email.includes('@')) {
                // Agar database mein Real Email hai (New User), toh use karo
                finalLoginEmail = userData.email;
            } else {
                // Agar Email GAYAB hai (Old User), toh Fake Email bana lo automatic
                finalLoginEmail = `${loginValue}@labourconnect.com`;
                console.log("⚠️ Old User Detected. Auto-generating login email:", finalLoginEmail);
            }
        }

        // Ab Email + Password se login karo
        await signInWithEmailAndPassword(auth, finalLoginEmail, formData.password);
        navigate('/');
      }
    } catch (error) {
      console.error("Auth Error:", error.code);
      
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setShowErrorPopup(true); // Show Stylish Popup
      } else if (error.code === 'auth/email-already-in-use') {
        alert("⚠️ Yeh Email pehle se registered hai. Login karein.");
      } else {
        alert("Error: " + error.message); // Other errors
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      
      {/* ✅✅ FORGOT PASSWORD MODAL ✅✅ */}
      {showForgotModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl relative">
                <button onClick={() => setShowForgotModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-red-500 font-bold">✕</button>
                
                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-3xl mx-auto mb-3">🔑</div>
                    <h3 className="text-xl font-extrabold text-slate-800">Reset Password</h3>
                    <p className="text-slate-500 text-sm">
                        Enter your registered Email ID. We will send a reset link.
                    </p>
                </div>

                <input 
                    type="email" 
                    placeholder="Enter your Email ID" 
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full p-4 bg-slate-50 rounded-xl outline-none border border-slate-200 focus:border-blue-500 mb-4"
                />

                <button 
                    onClick={handleForgotPassword}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold shadow-lg hover:bg-blue-700 transition"
                >
                    Send Reset Link
                </button>
            </div>
        </div>
      )}

      {/* ✅ CUSTOM ERROR POPUP (Overlay) */}
      {showErrorPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white w-full max-w-sm rounded-2xl p-6 shadow-2xl text-center relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-500 to-orange-500"></div>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">⚠️</div>
            <h3 className="text-xl font-extrabold text-slate-800 mb-2">Account Not Found</h3>
            <p className="text-slate-500 text-sm mb-6">
              Incorrect details. Please check your Mobile/Email or Password.
            </p>
            <div className="flex flex-col gap-3">
              <button 
                onClick={() => { setIsSignup(true); setShowErrorPopup(false); }}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold shadow-lg hover:bg-blue-700 transition"
              >
                Create New Account
              </button>
              <button 
                onClick={() => setShowErrorPopup(false)}
                className="w-full bg-slate-100 text-slate-600 py-3 rounded-xl font-bold hover:bg-slate-200 transition"
              >
                Try Again
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* --- LEFT SIDE: HERO IMAGE --- */}
      <div className="hidden lg:flex w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 z-0"><img src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=2070&auto=format&fit=crop" alt="Construction" className="w-full h-full object-cover opacity-40" /></div>
        <div className="relative z-10 p-12 text-white max-w-xl">
           <motion.div initial={{ x: -50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.8 }}>
             <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center text-3xl font-bold mb-6 shadow-lg">🇮🇳</div>
             <h1 className="text-5xl font-extrabold leading-tight mb-6">Building India, <br/><span className="text-yellow-400">One Click at a Time.</span></h1>
             <p className="text-lg text-slate-300 leading-relaxed mb-8">Connect with thousands of verified skilled workers instantly.</p>
           </motion.div>
        </div>
      </div>

      {/* --- RIGHT SIDE: FORM SECTION --- */}
      <div className="w-full lg:w-1/2 bg-slate-50 flex items-center justify-center p-6 relative py-20">
         <div className="lg:hidden absolute inset-0 bg-gradient-to-br from-blue-900 to-slate-900 z-0"></div>

         <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white/10 lg:bg-white backdrop-blur-lg lg:backdrop-blur-none border border-white/20 lg:border-none p-8 lg:p-12 rounded-3xl shadow-2xl lg:shadow-none w-full max-w-md relative z-10 lg:text-slate-800 text-white">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-extrabold mb-2">{isSignup ? 'Create Account' : 'Welcome Back'}</h2>
              <p className={`text-sm ${isSignup ? 'text-blue-200 lg:text-slate-500' : 'text-blue-200 lg:text-slate-500'}`}>{isSignup ? 'Join the network.' : 'Login with Mobile Number or Email.'}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              
              {isSignup && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div onClick={() => selectRole('customer')} className={`p-4 rounded-xl border-2 cursor-pointer text-center transition-all ${formData.role === 'customer' ? 'bg-blue-50 border-blue-600 text-blue-700 lg:bg-blue-50' : 'border-white/20 lg:border-slate-200 hover:bg-white/10'}`}>
                      <div className="text-2xl mb-1">🏠</div><div className="font-bold text-xs">Hire Workers</div>
                    </div>
                    <div onClick={() => selectRole('worker')} className={`p-4 rounded-xl border-2 cursor-pointer text-center transition-all ${formData.role === 'worker' ? 'bg-blue-50 border-blue-600 text-blue-700 lg:bg-blue-50' : 'border-white/20 lg:border-slate-200 hover:bg-white/10'}`}>
                      <div className="text-2xl mb-1">👷‍♂️</div><div className="font-bold text-xs">Find Work</div>
                    </div>
                  </div>

                  <input name="name" placeholder="Full Name" onChange={handleChange} className="w-full p-3.5 rounded-xl bg-white/10 lg:bg-slate-50 border border-white/20 lg:border-slate-200 outline-none focus:ring-2 focus:ring-blue-500 transition placeholder-slate-400 lg:text-black" required />
                  
                  <div className="grid grid-cols-2 gap-3">
                    <select name="state" onChange={handleChange} className="p-3.5 rounded-xl bg-white/10 lg:bg-slate-50 border border-white/20 lg:border-slate-200 text-slate-300 lg:text-slate-700 [&>option]:text-black outline-none" required>
                      <option value="">State</option>{Object.keys(statesAndDistricts).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select name="district" onChange={handleChange} className="p-3.5 rounded-xl bg-white/10 lg:bg-slate-50 border border-white/20 lg:border-slate-200 text-slate-300 lg:text-slate-700 [&>option]:text-black outline-none" required>
                      <option value="">District</option>{availableDistricts.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>

                  <div className="relative">
                    <span className="absolute left-4 top-3.5 font-bold text-sm text-slate-400 lg:text-slate-500">+91</span>
                    <input name="mobile" type="tel" value={formData.mobile} placeholder="Mobile Number" onChange={handleChange} className="w-full p-3.5 pl-12 rounded-xl bg-white/10 lg:bg-slate-50 border border-white/20 lg:border-slate-200 outline-none focus:ring-2 focus:ring-blue-500 font-semibold tracking-wide lg:text-black" required />
                  </div>
                  
                  <div className="relative">
                    <input name="email" type="email" placeholder="Email Address (Required)" onChange={handleChange} className="w-full p-3.5 rounded-xl bg-white/10 lg:bg-slate-50 border border-white/20 lg:border-slate-200 outline-none focus:ring-2 focus:ring-blue-500 lg:text-black" required />
                  </div>
                </>
              )}

              {/* ✅ LOGIN IDENTIFIER FIELD (LOGIN MODE ONLY) */}
              {!isSignup && (
                  <div className="relative">
                    <input 
                        name="loginIdentifier" 
                        type="text" 
                        placeholder="Email ID or Mobile Number" 
                        onChange={handleChange} 
                        className="w-full p-3.5 rounded-xl bg-white/10 lg:bg-slate-50 border border-white/20 lg:border-slate-200 outline-none focus:ring-2 focus:ring-blue-500 lg:text-black" 
                        required 
                    />
                  </div>
              )}

              {/* PASSWORD */}
              <div>
                  <div className="relative">
                    <input name="password" type={showPassword ? "text" : "password"} placeholder="Password" onChange={handleChange} className="w-full p-3.5 rounded-xl bg-white/10 lg:bg-slate-50 border border-white/20 lg:border-slate-200 outline-none focus:ring-2 focus:ring-blue-500 lg:text-black" required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-3.5 text-lg text-slate-400 hover:text-blue-500">{showPassword ? "🙈" : "👁️"}</button>
                  </div>
                  {!isSignup && (
                    <div className="text-right mt-2">
                        <button type="button" onClick={() => setShowForgotModal(true)} className="text-sm font-bold text-slate-300 lg:text-slate-500 hover:text-blue-400 lg:hover:text-blue-600 transition">Forgot Password?</button>
                    </div>
                  )}
              </div>

              <button disabled={loading} className="w-full bg-blue-600 lg:bg-slate-900 text-white py-4 rounded-xl font-bold hover:bg-blue-700 lg:hover:bg-blue-600 transition shadow-lg shadow-blue-500/30 lg:shadow-slate-900/20 text-lg">
                {loading ? 'Processing...' : (isSignup ? 'Create Account' : 'Login Now')}
              </button>
            </form>

            <div className="mt-8 text-center border-t border-white/10 lg:border-slate-100 pt-6">
              <p className="text-sm text-slate-300 lg:text-slate-500 mb-2">{isSignup ? "Already have an account?" : "New to KaamSetu?"}</p>
              <button onClick={() => setIsSignup(!isSignup)} className="text-yellow-400 lg:text-blue-600 font-bold hover:underline transition text-base">{isSignup ? "Login Here" : "Create New Account"}</button>
            </div>

            <div className="mt-6">
              <YouTubeHelpButton text="📺 See How to Login & Register" url="https://youtube.com/shorts/FibfRaMZGn4?si=T9FFXtrBL8rUar53" />
            </div>

         </motion.div>
      </div>
    </div>
  );
};

export default Signup;